/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-unstable-nested-components */
import './WebhooksList.scss';

import React, { FC, useContext, useEffect } from 'react';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, UniqueIdentifier, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CellContext, ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import ConnectionInfo from 'components/ConnectionInfo/ConnectionInfo';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useApiConnectionService } from 'customHooks/useApiConnectionService';
import { useDataSet } from 'customHooks/useDataSet';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { ApiConnection } from 'models/ApiConnection';
import { Connection } from 'models/Connection';
import { DocumentStatusKey } from 'models/DocumentStatusKey';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { DraggableRow, RowDragHandleCell } from '../DraggableConnectionTable/DraggableConnectionTable';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface WebhooksListProps {}

const WebhooksList: FC<WebhooksListProps> = () => {
  const {
    configRepo,
    selectedProjectId,
    notify,
    sendLog
  } = useContext(AppContext);

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const apiConnectionService = useApiConnectionService(configRepo!.PUBLISH_API_BASE_URI);

  const { dataSet, setOperators, operators } = useDataSet<ApiConnection>(
    configRepo!.PUBLISH_API_BASE_URI,
    Entities.ApiConnections,
    selectedProjectId,
  );
  const apiConnections: ApiConnection[] = dataSet.responseData as ApiConnection[];

  const resetOperators = (projectId: number) => {
    const ops = { ...operators };
    ops.ProjectId = projectId;
    setOperators(ops);
  };

  useEffect(() => {
    if (selectedProjectId && operators.ProjectId !== selectedProjectId) {
      resetOperators(selectedProjectId);
    }
  }, [selectedProjectId]);

  const testConnection = async (connection: Connection): Promise<boolean> => {  
    const result = await apiConnectionService.testConnection(connection as ApiConnection);
    if (result.isSuccessful) {
      notify("Connection test succeeded!", NotificationType.Success);
    } else {
      notify(`Connection failed due to error: ${result.errorType} ${result.errorMessage}`, NotificationType.Error);
    }
    resetOperators(connection.projectId);
    return result.isSuccessful;
  };

  const dataIds = React.useMemo<UniqueIdentifier[]>(
    () => apiConnections?.map(({ id }) => id as any),
    [apiConnections]
  );

  const renderFieldValue = (fieldName: string, value: CellContext<ApiConnection, unknown>) => 
     (<Form.Text>{value.getValue() as any}</Form.Text>);

  
  const renderConditions = (value: CellContext<ApiConnection, unknown>) => {
    const selectedOptions: DocumentStatusKey[] = value.getValue() as DocumentStatusKey[];
    return(selectedOptions.map(cond => <div className="condition" key={cond}>{cond.replace('_', ' ')}</div>))
  }

  const onTokenExpired = useTokenExpiredHandler();
  const { deleteEntity } = useModifyEntityService<ApiConnection>(
    configRepo!.PUBLISH_API_BASE_URI,
    Entities.ApiConnections,
    sendLog,
    onTokenExpired,
  );
  const onDelete = async (id: number): Promise<void> => {
    if (await deleteEntity(id)) {
      notify('Webhook has been deleted successfully.', NotificationType.Success);
      sendLog(`deleted successfully webhook: ${id}`, LogLevel.info);
      const ops = { ...operators };
      setOperators(ops);
    } else {
      notify('Webhook has not been deleted successfully.', NotificationType.Error);
      sendLog(`failed to delete webhook: ${id}`, LogLevel.error);
    }
  };
  const deleteMessage = 'Are you sure you want to delete this record?  This action is unreversible. ';
  
  const columns = React.useMemo<ColumnDef<ApiConnection>[]>(
    () => [
      {
        id: 'drag-handle',
        header: 'Reorder',
        cell: ({ row }) => 
          <div className='row-actions'>
            <RowDragHandleCell rowId={row.id}/>
            <Link 
              to={`/integrations/webhooks/edit/${row.id}`}
              className="btn btn-primary"
              title="Edit">Edit</Link>&nbsp;
            <Button variant="secondary" onClick={() => { testConnection(row.original); }}>Test</Button>&nbsp;
            <Button variant="danger" onClick={ (event: any) => {
              event.preventDefault();
              // eslint-disable-next-line no-alert
              if (window.confirm(deleteMessage)) {
                onDelete(parseInt(row.id, 10));
              }
             }}>Delete</Button>&nbsp;
          </div>
          ,
        size: 250,
      },
      {
        accessorKey: 'name',
        cell: info => renderFieldValue('name', info),
        header: 'Name'
      },
      {
        accessorKey: 'apiUrl',
        cell: info => renderFieldValue('apiUrl', info),
        header: 'Url',
      },
      {
        accessorKey: 'conditions',
        cell: info => renderConditions(info),
        header: 'Events',
      },
      {
        accessorKey: 'connectionStatus',
        cell: info => <ConnectionInfo connectionInfo={info.row.original} customActionName='Test Connection' customAction={testConnection} />,
        header: 'Status',
      }
    ],
    []
  )

  const table = useReactTable({
    data: apiConnections,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id! as any, // required because row indexes will change
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  // reorder rows after drag & drop
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (active && over && active.id !== over.id) {      
      let dataIdsCopy = [...dataIds];
      const oldIndex = dataIds.indexOf(active.id);
      const newIndex = dataIds.indexOf(over.id);
      dataIdsCopy = arrayMove(dataIdsCopy, oldIndex, newIndex);
      
      let orderId = 0;
      // eslint-disable-next-line no-plusplus
      const connectionsByOrderid: Array<{ id: number, orderId: number }> = dataIdsCopy.map(id => ({ id: (id as number), orderId: ++orderId }));
      const didUpdate = await apiConnectionService.reorderAll(connectionsByOrderid);
      if (!didUpdate) {
        notify('Failed to update order of webhooks.  Please refresh the page and try again.', NotificationType.Error)
      }
      resetOperators(selectedProjectId!);
    }
  }

  if (dataSet.isLoading) {
    return (<p>Loading...</p>);
  }

  if (dataSet.isError) {
    return (<p>Failed to load...</p>);
  }

  const heading: NavHeadingProps = {
    title: `Webhooks Configuration`,
    ...NavHeadings.Webhooks,
  };

  return (
    <div className="WebhooksList"> 
      <div className='container'>
        <div className='row'>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            // eslint-disable-next-line react/jsx-no-bind
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <div className="">
              <NavHeading {...heading} />
              <div className='row'>
                <div className="flex flex-wrap gap-2 ">
                  <Link to='/integrations/webhooks/edit/new' type="button" className="btn btn-primary pull-right">
                    <i className='bi bi-plus' />
                    Add New Connection
                  </Link>
                </div>
              </div>
              <div className='row'>
                {
                  (dataSet.responseData as ApiConnection[]).length === 0 ?
                  <div className='no-results'>
                    <p>
                      No webhooks configured yet.  Click on the &#39;Add New Connection&#39; button to setup your first webhook.
                    </p>
                  </div>
                  :
                  <table className="table">
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th key={header.id} colSpan={header.colSpan}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      <SortableContext
                        items={dataIds}
                        strategy={verticalListSortingStrategy}
                      >
                        {table.getRowModel().rows.map(row => (
                          <DraggableRow key={row.id} row={row} />
                        ))}
                      </SortableContext>
                    </tbody>
                  </table>
                }
              </div>
            </div>
          </DndContext>       
        </div>
      </div>
    </div>
  );
}

export default WebhooksList;
